// ✅ ENHANCED V2: src/pages/ClientPortalDashboard.jsx
// Fixes: Logo display, Invoice View/Download Modal, Payment History Tab
// ✅ FIX PORT-011 (Apr 11, 2026): Invoice crash — address object rendered as React child.
//   Added formatAddress() helper. Address is now safely serialized before rendering.
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Calendar, Clock, MapPin, FileText, ChevronRight, AlertCircle,
  Bell, LogOut, User, LayoutDashboard, Briefcase, CalendarDays,
  Receipt, Menu, X, Download, File, FileCheck, Scale, Gavel,
  Hash, Building, Users, BookOpen, AlertTriangle, IndianRupee,
  ChevronDown, ChevronUp, ExternalLink, CreditCard, Printer,
  Eye, CheckCircle, XCircle, Banknote, Wallet, CircleDollarSign,
  ShieldAlert
} from "lucide-react";

import { Badge, StatusBadge } from '../components/ui/Badge/Badge';
import { Card, CardHeader, CardBody } from '../components/ui/Card/Card';
import { Button } from '../components/ui/Button/Button';
// SPRINT-DPDP-DATA-RIGHTS-001 · Phase D — client-facing data rights entry points
import DataRightsPanel from '../components/portal/DataRightsPanel';
import { CF_BASE } from "../config/constants"; // v2.3.4

// ✅ Logo is in public folder - no import needed, use path directly

// ✅ FIX KAL-005 (Jun 24, 2026): PortalLogo extracted from inside ClientPortalDashboard.
// Previously defined as 'const Logo' inside the render function with its own useState —
// this violates React Rules of Hooks (hooks cannot be called inside nested functions
// defined during render). The component crashed silently after clientData loaded,
// keeping the loading spinner visible indefinitely.
// Fix: move to module level, replace useState with a ref-based onError approach.
function PortalLogo({ size = 'default' }) {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-10 h-10',
    large: 'w-12 h-12'
  };

  const [imgError, setImgError] = React.useState(false);

  return (
    <div className="flex items-center gap-3">
      {!imgError ? (
        <img
          src="/lexcore-icon.png"
          alt="LexCore Logo"
          className={`${sizeClasses[size]} object-contain`}
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          className={`${sizeClasses[size]} bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg`}
        >
          <Scale className="w-6 h-6 text-white" />
        </div>
      )}
      <span className="font-bold text-xl text-slate-900 tracking-tight">LexCore</span>
    </div>
  );
}

export default function ClientPortalDashboard() {
  const [searchParams] = useSearchParams();
  // SPRINT-DPDP-DATA-RIGHTS-001 · Phase D — same token already used by the
  // fetchData() effect below, just also kept at component scope so it can
  // be passed down to DataRightsPanel without re-deriving it.
  const portalToken = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCase, setSelectedCase] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    parties: true,
    legal: true,
    documents: true,
    financial: false,
  });
  
  // ✅ NEW: Invoice Modal State
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const invoiceRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setError("Missing portal token. Please use the secure email link.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${CF_BASE}/getClientData`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          }
        );

        if (!response.ok) {
          // ✅ FIX: Distinguish auth errors (401) from server crashes (500)
          // Previously ALL errors showed "Invalid or expired link" — misleading
          // for server-side failures like Firestore index errors (HTTP 500)
          if (response.status === 401) {
            setError("This link has expired or is invalid. Please request a new one from your law firm.");
          } else {
            const errBody = await response.json().catch(() => ({}));
            const errMsg = errBody?.error?.message || errBody?.error || response.statusText;
            setError(`Unable to load your portal. Please try again or contact your firm. (${response.status}: ${errMsg})`);
          }
          setLoading(false);
          return;
        }

        const result = await response.json();
        const responseData = result.result || result;

        if (responseData && responseData.success) {
          setClientData(responseData);
          if (responseData.cases?.length > 0) {
            setSelectedCase(responseData.cases[0]);
          }
        } else {
          setError("Failed to load client data. Please try again.");
        }
      } catch (err) {
        console.error("Portal fetch error:", err);
        // Network-level error (CORS, offline, DNS failure)
        setError("Cannot reach the server. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  // ✅ FIX PORT-011: formatAddress safely converts address object OR string to renderable string
  // Firestore stores address as {line1, line2, city, state, pin} — never render objects directly in React.
  const formatAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      return [address.line1, address.line2, address.city, address.state, address.pin]
        .filter(Boolean).join(', ') || null;
    }
    return null;
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ✅ Open Invoice Modal
  const openInvoiceModal = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceModalOpen(true);
  };

  // ✅ Close Invoice Modal
  const closeInvoiceModal = () => {
    setSelectedInvoice(null);
    setInvoiceModalOpen(false);
  };

  // ✅ Print Invoice
  const handlePrintInvoice = () => {
    const printContent = invoiceRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${selectedInvoice?.number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; }
            .invoice-header { display: flex; justify-content: space-between; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
            .logo { font-size: 24px; font-weight: bold; color: #2563eb; }
            .invoice-title { text-align: right; }
            .invoice-title h1 { font-size: 28px; color: #1e293b; }
            .invoice-title .number { color: #64748b; font-size: 14px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
            .info-section h3 { font-size: 12px; text-transform: uppercase; color: #64748b; margin-bottom: 8px; }
            .info-section p { font-size: 14px; line-height: 1.6; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            .items-table th { background: #f8fafc; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #64748b; border-bottom: 2px solid #e2e8f0; }
            .items-table td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
            .items-table .amount { text-align: right; }
            .totals { margin-left: auto; width: 300px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
            .totals-row.total { font-weight: bold; font-size: 18px; border-bottom: none; border-top: 2px solid #1e293b; padding-top: 12px; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .status-unpaid { background: #fef2f2; color: #dc2626; }
            .status-paid { background: #f0fdf4; color: #16a34a; }
            .status-partial { background: #fffbeb; color: #d97706; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; text-align: center; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ========== HELPER FUNCTIONS ==========
  const getEventTypeColor = (eventType) => {
    const colors = {
      Hearing: 'text-red-600 bg-red-50 border-red-200',
      Meeting: 'text-blue-600 bg-blue-50 border-blue-200',
      Deadline: 'text-orange-600 bg-orange-50 border-orange-200',
      Adjournment: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    };
    return colors[eventType] || 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const getEventTypeIcon = (eventType) => {
    const icons = { Hearing: '⚖️', Meeting: '👥', Deadline: '⏰', Adjournment: '📅' };
    return icons[eventType] || '📋';
  };

  // ✅ FIXED: Handle both string and object status values
  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700 border-green-200',
      hearing: 'bg-blue-100 text-blue-700 border-blue-200',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      closed: 'bg-slate-100 text-slate-700 border-slate-200',
      disposed: 'bg-purple-100 text-purple-700 border-purple-200',
      'in progress': 'bg-blue-100 text-blue-700 border-blue-200',
      filed: 'bg-green-100 text-green-700 border-green-200',
      dismissed: 'bg-red-100 text-red-700 border-red-200',
    };
    
    // Handle null/undefined
    if (!status) return colors.active;
    
    // Handle object status (from Firestore caseStatus object)
    if (typeof status === 'object') {
      // Try to extract status string from object properties
      const statusString = status.caseStatus || status.currentStage || status.status || 'active';
      return colors[statusString?.toString().toLowerCase()] || colors.active;
    }
    
    // Handle string status
    if (typeof status === 'string') {
      return colors[status.toLowerCase()] || colors.active;
    }
    
    return colors.active;
  };

  const getUrgencyColor = (level) => {
    const colors = {
      High: 'bg-red-100 text-red-700',
      Medium: 'bg-yellow-100 text-yellow-700',
      Low: 'bg-green-100 text-green-700',
    };
    return colors[level] || colors.Medium;
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      Cash: Banknote,
      Cheque: FileCheck,
      'Bank Transfer': Building,
      UPI: Wallet,
      Card: CreditCard,
      Online: CircleDollarSign,
    };
    return icons[method] || CreditCard;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      'Pending Clearance': 'bg-amber-100 text-amber-700',
      Cleared: 'bg-green-100 text-green-700',
      Bounced: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return date.toLocaleDateString('en-IN', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const isEventUpcoming = (dateString) => dateString && new Date(dateString) >= new Date();
  const isEventSoon = (dateString) => {
    if (!dateString) return false;
    const eventDate = new Date(dateString);
    const now = new Date();
    const hours = (eventDate - now) / (1000 * 60 * 60);
    return hours > 0 && hours <= 24;
  };

  const upcomingEvents = clientData?.events?.filter(e => isEventUpcoming(e.date)) || [];
  const pastEvents = clientData?.events?.filter(e => !isEventUpcoming(e.date)) || [];
  const clientName = clientData?.client?.name || clientData?.client?.companyName || "Client";

  // ========== LOADING STATE ==========
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-sm">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // ========== ERROR STATE ==========
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardBody className="text-center py-8">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Error</h3>
            <p className="text-sm text-slate-600 mb-6">{error}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>Try Again</Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  // ========== NAVIGATION ITEMS (Updated with Payments) ==========
  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'cases', label: 'My Cases', icon: Briefcase, badge: clientData?.cases?.length },
    { id: 'events', label: 'Events', icon: CalendarDays, badge: upcomingEvents.length },
    { id: 'invoices', label: 'Invoices', icon: Receipt, badge: clientData?.stats?.unpaidInvoices },
    { id: 'payments', label: 'Payments', icon: CreditCard, badge: clientData?.payments?.length }, // ✅ NEW
    // SPRINT-DPDP-DATA-RIGHTS-001 · Phase D
    { id: 'privacy', label: 'Data & Privacy', icon: ShieldAlert },
  ];

  // ========== COLLAPSIBLE SECTION COMPONENT ==========
  const CollapsibleSection = ({ title, icon: Icon, sectionKey, children, badge }) => (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-slate-500" />}
          <span className="font-semibold text-slate-900">{title}</span>
          {badge !== undefined && (
            <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="border-t border-slate-200 p-4">
          {children}
        </div>
      )}
    </div>
  );

  // ========== DETAIL ROW COMPONENT ==========
  const DetailRow = ({ label, value, icon: Icon, highlight }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-sm ${highlight ? 'font-semibold text-primary-600' : 'text-slate-900'}`}>
            {value}
          </p>
        </div>
      </div>
    );
  };

  // ✅ NEW: Helper function to extract status string from string or object
  const getStatusString = (status) => {
    if (!status) return 'Active';
    if (typeof status === 'string') return status;
    if (typeof status === 'object') {
      return status.caseStatus || status.currentStage || status.status || 'Active';
    }
    return 'Active';
  };

  // ========== CASE CARD COMPONENT ==========
  const CaseCard = ({ caseItem, isSelected, onClick }) => (
    <Card 
      hoverable 
      clickable 
      onClick={onClick} 
      className={`transition-all ${isSelected ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'}`}
    >
      <CardBody className="p-4">
        {caseItem.caseNumber && (
          <div className="flex items-center gap-2 mb-2">
            <Hash className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-mono font-bold text-primary-600">
              {caseItem.caseNumber}
            </span>
          </div>
        )}
        <h3 className="text-sm font-semibold text-slate-900 mb-2 line-clamp-2">
          {caseItem.title}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(caseItem.caseStatus || caseItem.status)}`}>
            {getStatusString(caseItem.caseStatus || caseItem.status)}
          </span>
          {caseItem.court && (
            <span className="text-xs text-slate-500">{caseItem.court}</span>
          )}
        </div>
        {caseItem.nextHearing && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-600">
            <Calendar className="w-3.5 h-3.5" />
            <span>Next: {formatDate(caseItem.nextHearing)}</span>
          </div>
        )}
      </CardBody>
    </Card>
  );

  // ========== LOGO COMPONENT ==========
  // ✅ FIX KAL-005 (Jun 24, 2026): Logo moved outside main component (see top of file).
  // useState inside a render-defined component violates Rules of Hooks → silent crash
  // after clientData loads. Using the PortalLogo component defined above instead.

  // ========== INVOICE DETAIL MODAL ==========
  const InvoiceModal = () => {
    if (!invoiceModalOpen || !selectedInvoice) return null;

    const inv = selectedInvoice;
    const statusClass = inv.status === 'paid' 
      ? 'status-paid' 
      : inv.status === 'partially_paid' 
        ? 'status-partial' 
        : 'status-unpaid';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={closeInvoiceModal}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-slate-50">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Invoice Details</h2>
              <p className="text-sm text-slate-500">#{inv.number || inv.invoiceNo}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handlePrintInvoice}
                leftIcon={<Printer className="w-4 h-4" />}
              >
                Print / Download
              </Button>
              <button 
                onClick={closeInvoiceModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Modal Body - Scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Printable Invoice Content */}
            <div ref={invoiceRef} className="p-8">
              {/* Invoice Header - Shows LAW FIRM details, not LexCore */}
              <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-slate-200">
                <div>
                  {/* Firm Logo & Name */}
                  <div className="flex items-center gap-3 mb-4">
                    {clientData?.firm?.logo ? (
                      <img 
                        src={clientData.firm.logo} 
                        alt="Firm Logo" 
                        className="w-14 h-14 object-contain"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center">
                        <Scale className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div>
                      <h1 className="text-2xl font-bold text-slate-900">
                        {clientData?.firm?.name || 'Your Law Firm'}
                      </h1>
                      {clientData?.firm?.website && (
                        <p className="text-sm text-slate-500">{clientData.firm.website}</p>
                      )}
                    </div>
                  </div>
                  {/* Firm Contact Details */}
                  <div className="text-sm text-slate-600 space-y-1">
                    {clientData?.firm?.email && (
                      <p>Email: {clientData.firm.email}</p>
                    )}
                    {clientData?.firm?.phone && (
                      <p>Phone: {clientData.firm.phone}</p>
                    )}
                    {clientData?.firm?.gstin && (
                      <p>GSTIN: {clientData.firm.gstin}</p>
                    )}
                    {clientData?.firm?.address && (
                      <p className="max-w-xs">{formatAddress(clientData.firm.address)}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-slate-900 mb-1">INVOICE</h2>
                  <p className="text-lg text-slate-600 font-mono">#{inv.number || inv.invoiceNo}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                    inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                    inv.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {(inv.status || 'unpaid')?.toString().replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Billing Info Grid */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Bill To</h3>
                  <p className="text-lg font-semibold text-slate-900">{clientName}</p>
                  <p className="text-sm text-slate-600">{clientData?.client?.email}</p>
                  {clientData?.client?.phone && (
                    <p className="text-sm text-slate-600">{clientData.client.phone}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Issue Date</h3>
                    <p className="text-sm text-slate-900">{formatDate(inv.issueDate)}</p>
                  </div>
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Due Date</h3>
                    <p className="text-sm text-slate-900 font-semibold">{formatDate(inv.dueDate)}</p>
                  </div>
                  {inv.caseTitle && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Related Case</h3>
                      <p className="text-sm text-slate-900">{inv.caseTitle}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items Table */}
              <div className="mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">SAC Code</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {inv.items && inv.items.length > 0 ? (
                      inv.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-4 text-sm text-slate-900">{item.description}</td>
                          <td className="px-4 py-4 text-sm text-slate-600 text-center font-mono">{item.sacCode || '—'}</td>
                          <td className="px-4 py-4 text-sm text-slate-600 text-center">{item.quantity}</td>
                          <td className="px-4 py-4 text-sm text-slate-900 text-right">{formatCurrency(item.rate)}</td>
                          <td className="px-4 py-4 text-sm text-slate-900 text-right font-semibold">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                          No line items available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals Section */}
              <div className="flex justify-end">
                <div className="w-80">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="text-slate-900">{formatCurrency(inv.subtotal)}</span>
                    </div>
                    {inv.tax && inv.tax.cgst > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">CGST (9%)</span>
                          <span className="text-slate-900">{formatCurrency(inv.tax.cgst)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">SGST (9%)</span>
                          <span className="text-slate-900">{formatCurrency(inv.tax.sgst)}</span>
                        </div>
                      </>
                    )}
                    {inv.tax && inv.tax.igst > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">IGST (18%)</span>
                        <span className="text-slate-900">{formatCurrency(inv.tax.igst)}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t-2 border-slate-900 pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-slate-900">Total Amount</span>
                      <span className="text-slate-900">{formatCurrency(inv.amount)}</span>
                    </div>
                  </div>
                  {inv.paidAmount > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">Amount Paid</span>
                        <span className="text-green-600 font-semibold">{formatCurrency(inv.paidAmount)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-red-600">Balance Due</span>
                        <span className="text-red-600 font-bold">{formatCurrency(inv.balanceAmount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes & Terms */}
              {(inv.notes || inv.terms) && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  {inv.notes && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Notes</h4>
                      <p className="text-sm text-slate-600">{inv.notes}</p>
                    </div>
                  )}
                  {inv.terms && (
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Terms & Conditions</h4>
                      <p className="text-sm text-slate-600">{inv.terms}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                <p className="text-xs text-slate-500">
                  Thank you for your business. For any queries, please contact your assigned advocate.
                </p>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-600">
              {inv.status === 'unpaid' && (
                <span className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  Payment pending - Please contact your firm for payment options
                </span>
              )}
              {inv.status === 'paid' && (
                <span className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  This invoice has been fully paid
                </span>
              )}
            </div>
            <Button variant="primary" onClick={closeInvoiceModal}>Close</Button>
          </div>
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Invoice Modal */}
      <InvoiceModal />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
              <PortalLogo size="small" />
              <button onClick={() => setSidebarOpen(false)} className="p-2 text-slate-600 hover:text-slate-900">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-6">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Welcome</p>
                <p className="font-semibold text-slate-900">{clientName}</p>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === item.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    {item.badge > 0 && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        activeTab === item.id ? 'bg-primary-200 text-primary-800' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:border-r lg:border-slate-200 lg:bg-white">
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <PortalLogo />
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="mb-6 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Welcome</p>
            <p className="font-semibold text-slate-900 truncate">{clientName}</p>
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === item.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </div>
                {item.badge > 0 && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    activeTab === item.id ? 'bg-primary-200 text-primary-800' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-4 border-t border-slate-200">
          <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
            <LogOut className="w-5 h-5" />
            Exit Portal
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 hover:text-slate-900">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">
              {navItems.find(item => item.id === activeTab)?.label}
            </h1>
          </div>
          <button className="relative p-2 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100">
            <Bell className="w-5 h-5" />
            {upcomingEvents.length > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            )}
          </button>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          
          {/* ========== OVERVIEW TAB ========== */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardBody className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Active Cases</p>
                        <p className="text-3xl font-bold text-slate-900">{clientData.cases?.length || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Upcoming Events</p>
                        <p className="text-3xl font-bold text-slate-900">{upcomingEvents.length}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <CalendarDays className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Unpaid Invoices</p>
                        <p className="text-3xl font-bold text-slate-900">{clientData.stats?.unpaidInvoices || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatCurrency(clientData.stats?.totalOutstanding || 0)} due
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
                {/* ✅ NEW: Payments Stats Card */}
                <Card>
                  <CardBody className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Total Payments</p>
                        <p className="text-3xl font-bold text-slate-900">{clientData.payments?.length || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatCurrency(clientData.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0)} paid
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Quick Access Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upcoming Events */}
                <Card>
                  <CardHeader 
                    title="Upcoming Events" 
                    action={
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setActiveTab('events')} 
                        rightIcon={<ChevronRight className="w-4 h-4" />}
                      >
                        View All
                      </Button>
                    } 
                    bordered 
                  />
                  <CardBody>
                    {upcomingEvents.length > 0 ? (
                      <div className="space-y-3">
                        {upcomingEvents.slice(0, 3).map((ev) => (
                          <div key={ev.id} className={`p-4 rounded-lg border-l-4 ${getEventTypeColor(ev.eventType)}`}>
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{getEventTypeIcon(ev.eventType)}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="default" size="sm">{ev.eventType}</Badge>
                                  {isEventSoon(ev.date) && <Badge variant="error" size="sm">Soon!</Badge>}
                                </div>
                                <h4 className="font-semibold text-slate-900 text-sm mb-1">{ev.title}</h4>
                                <p className="text-xs text-slate-600">
                                  📅 {formatEventDate(ev.date)} • 🕐 {formatEventTime(ev.date)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-600">No upcoming events</p>
                      </div>
                    )}
                  </CardBody>
                </Card>

                {/* My Cases */}
                <Card>
                  <CardHeader 
                    title="My Cases" 
                    action={
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setActiveTab('cases')} 
                        rightIcon={<ChevronRight className="w-4 h-4" />}
                      >
                        View All
                      </Button>
                    } 
                    bordered 
                  />
                  <CardBody>
                    {clientData.cases?.length > 0 ? (
                      <div className="space-y-3">
                        {clientData.cases.slice(0, 3).map((c) => (
                          <div 
                            key={c.id} 
                            onClick={() => { setSelectedCase(c); setActiveTab('cases'); }} 
                            className="p-4 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {c.caseNumber && (
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <Hash className="w-3.5 h-3.5 text-primary-500" />
                                    <span className="text-xs font-mono font-bold text-primary-600">
                                      {c.caseNumber}
                                    </span>
                                  </div>
                                )}
                                <h4 className="font-semibold text-slate-900 text-sm mb-1">{c.title}</h4>
                                <div className="flex items-center gap-2">
                                  <StatusBadge status={getStatusString(c.caseStatus || c.status)} size="sm" />
                                  {c.court && (
                                    <span className="text-xs text-slate-500">{c.court}</span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-600">No active cases</p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </div>
          )}

          {/* ========== CASES TAB ========== */}
          {activeTab === 'cases' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Case List */}
                <div className="lg:col-span-1 space-y-3">
                  <div className="mb-4">
                    <p className="text-sm text-slate-500">
                      {clientData.cases?.length || 0} case{clientData.cases?.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                  {clientData.cases?.map((c) => (
                    <CaseCard 
                      key={c.id} 
                      caseItem={c} 
                      isSelected={selectedCase?.id === c.id}
                      onClick={() => setSelectedCase(c)}
                    />
                  ))}
                  {(!clientData.cases || clientData.cases.length === 0) && (
                    <div className="text-center py-12 text-slate-500">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No cases found</p>
                    </div>
                  )}
                </div>

                {/* Case Details */}
                <div className="lg:col-span-2">
                  {selectedCase ? (
                    <div className="space-y-4">
                      {/* Case Header Card */}
                      <Card>
                        <CardBody className="p-6">
                          {selectedCase.caseNumber && (
                            <div className="flex items-center gap-2 mb-3">
                              <Hash className="w-5 h-5 text-primary-500" />
                              <span className="text-lg font-mono font-bold text-primary-600">
                                {selectedCase.caseNumber}
                              </span>
                              {selectedCase.caseYear && (
                                <span className="text-sm text-slate-500">
                                  ({selectedCase.caseYear})
                                </span>
                              )}
                            </div>
                          )}
                          <h2 className="text-xl font-bold text-slate-900 mb-3">
                            {selectedCase.title}
                          </h2>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedCase.caseStatus || selectedCase.status)}`}>
                              {getStatusString(selectedCase.caseStatus || selectedCase.status)}
                            </span>
                            {selectedCase.urgencyLevel && (
                              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getUrgencyColor(selectedCase.urgencyLevel)}`}>
                                {selectedCase.urgencyLevel} Priority
                              </span>
                            )}
                          </div>
                          {selectedCase.nextHearing && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                              <Calendar className="w-5 h-5 text-amber-600" />
                              <div>
                                <p className="text-sm font-semibold text-amber-800">Next Hearing</p>
                                <p className="text-sm text-amber-700">{formatDate(selectedCase.nextHearing)}</p>
                              </div>
                            </div>
                          )}
                        </CardBody>
                      </Card>

                      {/* Case Details Section */}
                      <CollapsibleSection title="Case Details" icon={Scale} sectionKey="details">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <DetailRow label="Court" value={selectedCase.court} icon={Building} />
                          <DetailRow label="Court Location" value={selectedCase.courtName || selectedCase.location} icon={MapPin} />
                          <DetailRow label="Case Type" value={selectedCase.caseType} icon={FileText} />
                          <DetailRow label="Category" value={selectedCase.category} icon={FileCheck} />
                          <DetailRow label="Judge" value={selectedCase.judgeName} icon={Gavel} />
                          <DetailRow label="Filing Date" value={formatDate(selectedCase.filingDate)} icon={Calendar} />
                        </div>
                        {selectedCase.description && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Description</p>
                            <p className="text-sm text-slate-700">{selectedCase.description}</p>
                          </div>
                        )}
                      </CollapsibleSection>

                      {/* Parties Section */}
                      <CollapsibleSection title="Parties Information" icon={Users} sectionKey="parties">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <DetailRow label="Opposite Party" value={selectedCase.oppositePartyName} icon={Users} />
                          <DetailRow label="Opposite Advocate" value={selectedCase.oppositeAdvocate} icon={User} />
                          {selectedCase.advocateOnRecord && (
                            <DetailRow label="Your Advocate" value={selectedCase.advocateOnRecord.name} icon={Scale} highlight />
                          )}
                        </div>
                      </CollapsibleSection>

                      {/* Legal Information Section */}
                      {(selectedCase.acts?.length > 0 || selectedCase.sections?.length > 0) && (
                        <CollapsibleSection title="Legal Information" icon={BookOpen} sectionKey="legal">
                          <div className="space-y-4">
                            {selectedCase.acts?.length > 0 && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Acts</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedCase.acts.map((act, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                                      {act}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selectedCase.sections?.length > 0 && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Sections</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedCase.sections.map((section, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full border border-purple-200">
                                      Section {section}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CollapsibleSection>
                      )}

                      {/* Documents Section — ✅ FIX PORT-008 (Apr 11, 2026) */}
                      {/* Root cause: old code read caseData.documents[] (always empty). */}
                      {/* Fix: getClientData.js now fetches from /documents subcollection. */}
                      <CollapsibleSection title="Documents" icon={FileText} sectionKey="documents" badge={selectedCase.documentCount}>
                        {selectedCase.documents?.length > 0 ? (
                          <div className="space-y-2">
                            {selectedCase.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-slate-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {doc.documentType && (
                                        <span className="text-[10px] font-medium bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                                          {doc.documentType}
                                        </span>
                                      )}
                                      {doc.fileSize && (
                                        <span className="text-xs text-slate-400">
                                          {doc.fileSize < 1024 * 1024
                                            ? `${(doc.fileSize / 1024).toFixed(1)} KB`
                                            : `${(doc.fileSize / 1024 / 1024).toFixed(1)} MB`}
                                        </span>
                                      )}
                                      {doc.uploadedAt && (
                                        <span className="text-xs text-slate-400">{formatDate(doc.uploadedAt)}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {doc.fileUrl ? (
                                    <>
                                      <a
                                        href={doc.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="View document"
                                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </a>
                                      <a
                                        href={doc.fileUrl}
                                        download={doc.name}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Download document"
                                        className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      >
                                        <Download className="w-4 h-4" />
                                      </a>
                                    </>
                                  ) : (
                                    <span className="text-xs text-red-400 italic px-2">URL missing</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No documents available</p>
                            <p className="text-xs text-slate-400 mt-1">Documents shared by your firm will appear here.</p>
                          </div>
                        )}
                      </CollapsibleSection>

                      {/* Financial Summary Section */}
                      {selectedCase.financial && (
                        <CollapsibleSection title="Financial Summary" icon={IndianRupee} sectionKey="financial">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Billed</p>
                              <p className="text-lg font-bold text-slate-900">
                                {formatCurrency(selectedCase.financial.totalBilled)}
                              </p>
                            </div>
                            <div className="p-4 bg-amber-50 rounded-lg">
                              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Outstanding</p>
                              <p className="text-lg font-bold text-amber-700">
                                {formatCurrency(selectedCase.financial.outstanding)}
                              </p>
                            </div>
                          </div>
                        </CollapsibleSection>
                      )}
                    </div>
                  ) : (
                    <Card>
                      <CardBody className="text-center py-16">
                        <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600">Select a case to view details</p>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ========== EVENTS TAB ========== */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Upcoming Events ({upcomingEvents.length})
                </h3>
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingEvents.map((ev) => (
                      <Card key={ev.id}>
                        <CardBody className={`p-6 border-l-4 ${getEventTypeColor(ev.eventType)}`}>
                          <div className="flex items-start gap-4">
                            <span className="text-3xl">{getEventTypeIcon(ev.eventType)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="default">{ev.eventType}</Badge>
                                {isEventSoon(ev.date) && <Badge variant="error">Soon!</Badge>}
                                {ev.isGeneralMeeting && <Badge variant="secondary">Meeting</Badge>}
                              </div>
                              <h4 className="text-lg font-semibold text-slate-900 mb-2">{ev.title}</h4>
                              {ev.description && (
                                <p className="text-sm text-slate-600 mb-3">{ev.description}</p>
                              )}
                              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatEventDate(ev.date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatEventTime(ev.date)}
                                </span>
                                {ev.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {ev.location}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardBody className="text-center py-12">
                      <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600">No upcoming events scheduled</p>
                    </CardBody>
                  </Card>
                )}
              </div>

              {pastEvents.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Past Events ({pastEvents.length})
                  </h3>
                  <div className="space-y-3 opacity-75">
                    {pastEvents.slice(0, 5).map((ev) => (
                      <Card key={ev.id}>
                        <CardBody className="p-4">
                          <div className="flex items-center gap-3">
                            <span className="text-xl opacity-50">{getEventTypeIcon(ev.eventType)}</span>
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-700">{ev.title}</h4>
                              <p className="text-sm text-slate-500">{formatEventDate(ev.date)}</p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========== INVOICES TAB (ENHANCED) ========== */}
          {activeTab === 'invoices' && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Total Invoices</p>
                    <p className="text-2xl font-bold text-slate-900">{clientData.invoices?.length || 0}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Unpaid</p>
                    <p className="text-2xl font-bold text-amber-600">{clientData.stats?.unpaidInvoices || 0}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Outstanding Amount</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(clientData.stats?.totalOutstanding || 0)}
                    </p>
                  </CardBody>
                </Card>
              </div>

              {/* Invoice List */}
              <div className="space-y-4">
                {clientData.invoices?.length > 0 ? (
                  clientData.invoices.map((inv) => (
                    <Card key={inv.id} hoverable className="cursor-pointer" onClick={() => openInvoiceModal(inv)}>
                      <CardBody className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-bold text-slate-900">Invoice #{inv.number || inv.invoiceNo}</h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                inv.status === 'paid' 
                                  ? 'bg-green-100 text-green-700' 
                                  : inv.status === 'partially_paid'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {(inv.status || 'unpaid')?.toString().replace('_', ' ')}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                              <span>Issued: {formatDate(inv.issueDate)}</span>
                              <span>Due: {formatDate(inv.dueDate)}</span>
                              {inv.caseTitle && <span>Case: {inv.caseTitle}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-slate-900">
                                {formatCurrency(inv.amount)}
                              </p>
                              {inv.balanceAmount > 0 && inv.status !== 'paid' && (
                                <p className="text-sm text-red-600">
                                  Balance: {formatCurrency(inv.balanceAmount)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="secondary" 
                                size="sm"
                                leftIcon={<Eye className="w-4 h-4" />}
                                onClick={(e) => { e.stopPropagation(); openInvoiceModal(inv); }}
                              >
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardBody className="text-center py-12">
                      <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600">No invoices found</p>
                    </CardBody>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* ========== PAYMENTS TAB (NEW) ========== */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              {/* Payment Stats - Enhanced with Balance Due */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Total Payments</p>
                    <p className="text-2xl font-bold text-slate-900">{clientData.payments?.length || 0}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Total Paid</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(clientData.stats?.totalPaid || 0)}
                    </p>
                  </CardBody>
                </Card>
                {/* ✅ NEW: Balance Due Card */}
                <Card className="border-red-200 bg-red-50/30">
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Balance Due</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(clientData.stats?.totalOutstanding || 0)}
                    </p>
                    {clientData.stats?.unpaidInvoices > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {clientData.stats.unpaidInvoices} unpaid invoice{clientData.stats.unpaidInvoices !== 1 ? 's' : ''}
                      </p>
                    )}
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <p className="text-sm text-slate-500 mb-1">Pending Clearance</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {clientData.stats?.pendingClearanceCount || 0}
                    </p>
                    {clientData.stats?.pendingClearanceAmount > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCurrency(clientData.stats.pendingClearanceAmount)}
                      </p>
                    )}
                  </CardBody>
                </Card>
              </div>

              {/* Financial Summary Card */}
              <Card>
                <CardHeader title="Financial Summary" bordered />
                <CardBody className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Billed</p>
                      <p className="text-xl font-bold text-slate-900">
                        {formatCurrency(clientData.stats?.totalBilled || 0)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Paid</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(clientData.stats?.totalPaid || 0)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Balance Due</p>
                      <p className="text-xl font-bold text-red-600">
                        {formatCurrency(clientData.stats?.totalOutstanding || 0)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Pending Clearance</p>
                      <p className="text-xl font-bold text-amber-600">
                        {formatCurrency(clientData.stats?.pendingClearanceAmount || 0)}
                      </p>
                    </div>
                  </div>
                  {/* Advance Payments Notice */}
                  {clientData.stats?.advancePayments > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-semibold text-blue-800">Advance Payment Available</p>
                        <p className="text-sm text-blue-700">
                          You have {formatCurrency(clientData.stats.advancePayments)} in advance payments that can be applied to future invoices.
                        </p>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Payment History List */}
              <Card>
                <CardHeader title="Payment History" bordered />
                <CardBody className="p-0">
                  {clientData.payments?.length > 0 ? (
                    <div className="divide-y divide-slate-200">
                      {clientData.payments.map((payment) => {
                        const MethodIcon = getPaymentMethodIcon(payment.method);
                        return (
                          <div key={payment.id} className="p-4 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <MethodIcon className="w-6 h-6 text-slate-600" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold text-slate-900">
                                      {payment.paymentNumber || `PAY-${payment.id.substring(0, 8)}`}
                                    </p>
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPaymentStatusColor(payment.clearanceStatus || payment.status)}`}>
                                      {(payment.clearanceStatus || payment.status || 'pending')?.toString()}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3.5 h-3.5" />
                                      {formatDate(payment.paymentDate)}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <CreditCard className="w-3.5 h-3.5" />
                                      {payment.method}
                                    </span>
                                    {payment.invoiceId && (
                                      <span className="flex items-center gap-1">
                                        <Receipt className="w-3.5 h-3.5" />
                                        Invoice linked
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-green-600">
                                  {formatCurrency(payment.amount)}
                                </p>
                                {payment.receiptNumber && (
                                  <p className="text-xs text-slate-500">
                                    Receipt: {payment.receiptNumber}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600">No payment history found</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Payments will appear here once recorded
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          )}

          {/* ========== DATA & PRIVACY TAB (SPRINT-DPDP-DATA-RIGHTS-001 · Phase D) ========== */}
          {activeTab === 'privacy' && (
            <div className="space-y-6 max-w-xl">
              <DataRightsPanel portalToken={portalToken} clientData={clientData?.client} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}